// src/components/billing/PacksPanel.jsx — one-off credit packs.
//
// ⛔⛔ PACKS POOL. Purchased credits are ONE running total; which pack a credit
// came from has no answer anywhere in the system. Never render a list of packs
// each with a remaining balance, never imply an order of use or expiry, and say
// "i tuoi crediti acquistati", never "il tuo pacchetto".
//
// ⛔ Nothing is rendered when `packs` is EMPTY — an environment whose Stripe
// catalogue was never seeded: a state, not an error.
//
// ⭐ 2026-09-29: the "Acquista" button is ALWAYS there, DISABLED while the backend
// says packs are not available to this customer (`packs_available: false`), with
// the reason worded by US from `packs_reason_code` — ⛔ never the server's
// `packs_reason`, which is English prose. The four codes are the backend's own
// per-customer chain (billing_pack_eligibility.py), in its order; an unknown code
// prints the code as a handle for support. CUSTOMER_NOT_INVOICEABLE also links to
// the invoicing details (`billingDetailsTo`, the same prop PlanCard takes).
//
// ⚠️ `credits` and `price` are Decimal STRINGS. The price is shown like a plan
// card's — WITHOUT decimals (`formatPrice`) and net of VAT ("+ IVA"); no
// arithmetic on either.
//
// v203: the subtitle speaks of «crediti del periodo», never the «allowance» jargon; a pack
// purchase is confirmed first by the page (BillingConfirmDialog, `packs_disclosure_codes`) —
// ⛔ no «Circa X crediti» estimate next to the button.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Coins } from 'lucide-react';
import SectionCard, { CARD_BODY } from '../ui/SectionCard.jsx';
import { CARD_GRID } from './cardGrid.js';
import { formatCredits, formatPrice } from '../../utils/creditsFormat.js';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

/** `packs_reason_code` → our sentence (billing namespace). */
export const PACK_REASON_KEY = {
  NO_SUBSCRIPTION: 'packs.reasons.noSubscription',
  PACK_REQUIRES_PAID_PLAN: 'packs.reasons.requiresPaidPlan',
  CUSTOMER_NOT_INVOICEABLE: 'packs.reasons.notInvoiceable',
  NO_PAYMENT_ACCOUNT: 'packs.reasons.noPaymentAccount',
};

const REASON_ID = 'packs-unavailable-reason';

function reasonText(t, code) {
  if (PACK_REASON_KEY[code]) return t(PACK_REASON_KEY[code]);
  return code ? t('packs.reasons.generic', { code }) : t('packs.reasons.unavailable');
}

export default function PacksPanel({
  packs, available, reasonCode, busy, onBuy, billingDetailsTo = '/billing-details',
}) {
  const { t } = useTranslation('billing');
  if (!packs?.length) return null;
  const blocked = !available;
  const disabled = blocked || busy;
  return (
    // ⭐ 2026-10-08: its own section card (icon + title + subtitle), like the plans card above it.
    <SectionCard icon={Coins} title={t('packs.title')} subtitle={t('packs.subtitle')} style={{ marginTop: 24 }}>
    <div style={{ ...CARD_BODY, fontFamily: FONT_SANS }}>
      {blocked && (
        <p id={REASON_ID} style={{ fontSize: 12, color: COLORS.textSecondary, margin: '0 0 12px' }}>
          {reasonText(t, reasonCode)}
          {reasonCode === 'CUSTOMER_NOT_INVOICEABLE' && (
            <> <Link to={billingDetailsTo} style={{ color: COLORS.accent, fontWeight: 600 }}>
              {t('plan.completeInvoiceDetails')}
            </Link></>
          )}
        </p>
      )}
      {/* The plan cards' grid (cardGrid.js): same wrapping at every width. */}
      <div data-testid="card-grid" style={CARD_GRID}>
        {packs.map((p) => (
          <div key={p.pack_id} style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: RADII.control, padding: '18px 20px', opacity: blocked ? 0.6 : 1,
          }}>
            {/* ⭐ 2026-10-08: laid out like a plan card — the title big and centred, the price smaller and centred,
                the credits on the line under it (as «… crediti per periodo» on a plan card). */}
            <h3 style={{ fontSize: 22, fontWeight: 700, margin: 0, textAlign: 'center' }}>{t('packs.cardTitle')}</h3>
            <div style={{ marginTop: 10, fontSize: 16, fontWeight: 700, textAlign: 'center' }}>
              {formatPrice(p.price) ?? p.price} {p.currency}
              <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.textSecondary }}>
                {' '}{t('plan.vatExcluded')}
              </span>
            </div>
            <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 6 }}>
              {t('packs.credits', { credits: formatCredits(p.credits) })}
            </div>
            <button
              type="button" disabled={disabled} onClick={() => onBuy(p)}
              aria-describedby={blocked ? REASON_ID : undefined}
              style={{
                marginTop: 14, width: '100%', padding: '10px', border: 'none',
                borderRadius: RADII.controlInner, background: disabled ? COLORS.borderStrong : COLORS.accent,
                color: COLORS.surface, fontWeight: 600, fontSize: 14,
                cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS,
              }}
            >
              {t('packs.buy')}
            </button>
          </div>
        ))}
      </div>
    </div>
    </SectionCard>
  );
}
